import { PatientEncounter } from '../../patient/components/models/patient-encounter';
import { PatientViewModel } from '../../patient/components/models/patient-view-model';
import { PrimaryAddress } from '../../shared/model/primary-address';
import { phoneNumberValidator } from '../../shared/misc/regex';

/**
 * Check if IOC has all required fields completed.
 * Note: Risk class check is skipped as it's not required for enrollment via /pay
 */
export const checkIOCComplete = (encounter: PatientEncounter | null): boolean => {
  if (!encounter) return false;
  
  return (
    encounter.patientId > 0 &&
    encounter.clientId !== -1 &&
    encounter.clientId !== null &&
    encounter.payorRisk?.payorRiskId > 0 &&
    encounter.timingRisk?.timingRiskId > 0 &&
    encounter.pfrAmt > 0 &&
    encounter.workflow?.workflowId > 0
  );
};

/**
 * Get list of missing IOC fields.
 */
export const getMissingIOCFields = (encounter: PatientEncounter | null): string[] => {
  if (!encounter) return ['IOC data not loaded'];
  
  const missing: string[] = [];
  
  if (!(encounter.clientId !== -1 && encounter.clientId !== null)) missing.push('Client');
  if (!(encounter.payorRisk?.payorRiskId > 0)) missing.push('Payor Risk');
  if (!(encounter.timingRisk?.timingRiskId > 0)) missing.push('Timing Risk');
  if (!(encounter.pfrAmt > 0)) missing.push('PFR Amount');
  if (!(encounter.workflow?.workflowId > 0)) missing.push('Workflow');
  
  return missing;
};

/**
 * Check if patient demographics are complete.
 * Required: first name, last name, and date of birth.
 */
export const checkDemographicsComplete = (patient: PatientViewModel | null): boolean => {
  if (!patient?.contact) return false;
  
  const { firstName, lastName, birthDate } = patient.contact;
  
  return (
    firstName !== '' &&
    firstName !== null &&
    lastName !== '' &&
    lastName !== null &&
    birthDate !== '' &&
    birthDate !== null
  );
};

/**
 * Get list of missing demographic fields.
 * Required: first name, last name, and date of birth.
 */
export const getMissingDemographicFields = (patient: PatientViewModel | null): string[] => {
  if (!patient?.contact) return ['Patient data not loaded'];
  
  const missing: string[] = [];
  const { firstName, lastName, birthDate } = patient.contact;
  
  if (!firstName || firstName === '') missing.push('First Name');
  if (!lastName || lastName === '') missing.push('Last Name');
  if (!birthDate || birthDate === '') missing.push('Date of Birth');
  
  return missing;
};

export interface NewGuarantorFormData {
  phone: string;
  birthDate: string;
  relationshipToPatient: string;
  address: {
    streetAddress1: string;
    city: string;
    state: string;
    zipCode: string;
  };
}

export interface ExistingGuarantorFormData {
  phone: string;
  birthDate: string;
  relationshipToPatient: string;
  address: {
    streetAddress1: string;
    city: string;
    state: string;
    zipCode: string;
  };
}

/**
 * Get list of missing account holder fields.
 */
export const getMissingAccountHolderFields = (
  encounter: PatientEncounter | null,
  patient: PatientViewModel | null,
  isCreatingNewGuarantor?: boolean,
  newGuarantorData?: NewGuarantorFormData,
  existingGuarantorFormData?: ExistingGuarantorFormData
): string[] => {
  if (!encounter || !patient) return ['Account holder data not loaded'];

  const missing: string[] = [];

  // If creating a new guarantor, check form data
  if (isCreatingNewGuarantor && newGuarantorData) {
    if (!newGuarantorData.phone?.trim() || !phoneNumberValidator.test(newGuarantorData.phone)) {
      missing.push('Phone Number');
    }
    if (!newGuarantorData.birthDate?.trim()) {
      missing.push('Date of Birth');
    }
    if (!newGuarantorData.relationshipToPatient?.trim()) {
      missing.push('Relationship to Patient');
    }
    if (!newGuarantorData.address?.streetAddress1?.trim()) {
      missing.push('Street Address');
    }
    if (!newGuarantorData.address?.city?.trim()) {
      missing.push('City');
    }
    if (!newGuarantorData.address?.state?.trim()) {
      missing.push('State');
    }
    if (!newGuarantorData.address?.zipCode?.trim()) {
      missing.push('Zip Code');
    }
    return missing;
  }

  if (existingGuarantorFormData) {
    if (!existingGuarantorFormData.phone?.trim() || !phoneNumberValidator.test(existingGuarantorFormData.phone)) {
      missing.push('Phone Number');
    }
    if (!existingGuarantorFormData.birthDate?.trim()) {
      missing.push('Date of Birth');
    }
    if (!existingGuarantorFormData.address?.streetAddress1?.trim()) {
      missing.push('Street Address');
    }
    if (!existingGuarantorFormData.address?.city?.trim()) {
      missing.push('City');
    }
    if (!existingGuarantorFormData.address?.state?.trim()) {
      missing.push('State');
    }
    if (!existingGuarantorFormData.address?.zipCode?.trim()) {
      missing.push('Zip Code');
    }
    return missing;
  }

  // Otherwise, check existing guarantor or patient from database
  const thirdPartyGuarantor = encounter.patientChampion?.filter(
    (champion) => champion.isGuarantor
  ) || [];
  
  const hasGuarantor = thirdPartyGuarantor.length > 0;
  const accountHolderContact = hasGuarantor 
    ? thirdPartyGuarantor[0]?.contact 
    : patient.contact;
  
  const accountHolderAddress = accountHolderContact?.primaryAddress;
  const accountHolderPhone = accountHolderContact?.primPhoneNum;
  const accountHolderDob = accountHolderContact?.birthDate;
  const relationshipToPatient = hasGuarantor ? thirdPartyGuarantor[0]?.relationshipToPatient : null;

  if (!accountHolderPhone || accountHolderPhone === '' || !phoneNumberValidator.test(accountHolderPhone)) {
    missing.push('Phone Number');
  }
  if (!accountHolderDob || accountHolderDob === '') {
    missing.push('Date of Birth');
  }
  if (hasGuarantor && (!relationshipToPatient || relationshipToPatient === '')) {
    missing.push('Relationship to Patient');
  }
  if (!accountHolderAddress?.streetAddress1) {
    missing.push('Street Address');
  }
  if (!accountHolderAddress?.city) {
    missing.push('City');
  }
  if (!accountHolderAddress?.state?.stateCode) {
    missing.push('State');
  }
  if (!accountHolderAddress?.zipCode) {
    missing.push('Zip Code');
  }

  return missing;
};

/**
 * Check if the account holder's information is complete.
 * Account holder is either:
 * 1. A new guarantor being created (validate from form data)
 * 2. An existing guarantor with form data (validate from form data)
 * 3. An existing guarantor (validate from encounter data)
 * 4. The patient (validate from patient data)
 * Required: address, phone number, date of birth, and relationship to patient (for new guarantors only).
 */
export const checkAccountHolderAddress = (
  encounter: PatientEncounter | null,
  patient: PatientViewModel | null,
  isCreatingNewGuarantor?: boolean,
  newGuarantorData?: NewGuarantorFormData,
  existingGuarantorFormData?: ExistingGuarantorFormData
): boolean => {
  if (!encounter || !patient) return false;

  // If creating a new guarantor, validate from form data
  if (isCreatingNewGuarantor && newGuarantorData) {
    const hasValidAddress = !!(
      newGuarantorData.address?.streetAddress1?.trim() && 
      newGuarantorData.address?.city?.trim() && 
      newGuarantorData.address?.zipCode?.trim() && 
      newGuarantorData.address?.state?.trim()
    );

    const hasValidPhone = !!(
      newGuarantorData.phone?.trim() &&
      phoneNumberValidator.test(newGuarantorData.phone)
    );

    const hasValidDob = !!(newGuarantorData.birthDate?.trim());

    const hasValidRelationship = !!(newGuarantorData.relationshipToPatient?.trim());

    return hasValidAddress && hasValidPhone && hasValidDob && hasValidRelationship;
  }

  // If form data is provided for an existing guarantor (patient or champion), validate against form data
  if (existingGuarantorFormData) {
    const hasValidAddress = !!(
      existingGuarantorFormData.address?.streetAddress1?.trim() && 
      existingGuarantorFormData.address?.city?.trim() && 
      existingGuarantorFormData.address?.zipCode?.trim() && 
      existingGuarantorFormData.address?.state?.trim()
    );

    const hasValidPhone = !!(
      existingGuarantorFormData.phone?.trim() &&
      phoneNumberValidator.test(existingGuarantorFormData.phone)
    );

    const hasValidDob = !!(existingGuarantorFormData.birthDate?.trim());

    return hasValidAddress && hasValidPhone && hasValidDob;
  }

  // Otherwise, validate from existing guarantor or patient from database
  const thirdPartyGuarantor = encounter.patientChampion?.filter(
    (champion) => champion.isGuarantor
  ) || [];
  
  const hasGuarantor = thirdPartyGuarantor.length > 0;
  const accountHolderContact = hasGuarantor 
    ? thirdPartyGuarantor[0]?.contact 
    : patient.contact;
  
  const accountHolderAddress: PrimaryAddress = accountHolderContact?.primaryAddress;
  const accountHolderPhone = accountHolderContact?.primPhoneNum;
  const accountHolderDob = accountHolderContact?.birthDate;
  const relationshipToPatient = hasGuarantor ? thirdPartyGuarantor[0]?.relationshipToPatient : null;

  const hasValidAddress = !!(
    accountHolderAddress?.streetAddress1 && 
    accountHolderAddress?.city && 
    accountHolderAddress?.zipCode && 
    accountHolderAddress?.state?.stateCode
  );

  const hasValidPhone = !!(
    accountHolderPhone &&
    accountHolderPhone !== '' &&
    phoneNumberValidator.test(accountHolderPhone)
  );

  const hasValidDob = !!(accountHolderDob && accountHolderDob !== '');

  const hasValidRelationship = hasGuarantor 
    ? !!(relationshipToPatient && relationshipToPatient !== '')
    : true;

  return hasValidAddress && hasValidPhone && hasValidDob && hasValidRelationship;
};

/**
 * Check if PFR matches between IOC and payment program.
 */
export const checkPFRMatch = (encounter: PatientEncounter | null): boolean => {
  if (!encounter) return false;
  
  const program = encounter.patientPaymentProgram?.[
    encounter.patientPaymentProgram.length - 1
  ];
  
  if (!program) return false;
  
  return program.patientPaymentSchedule?.pfrAmt === encounter.pfrAmt;
};

/**
 * Check if the program is NOT a skeleton program.
 * A skeleton program is detected when:
 * - It's not a full-pay plan (paymentFreq !== 'F')
 * - AND both down payment is 0 AND (recurring amount < 1 OR terms < 1)
 * 
 * For non-full-pay programs, we require either:
 * - Recurring payment amount >= 1, OR
 * - Terms >= 1
 * 
 * @returns true if the program is valid (not a skeleton), false if skeleton detected
 */
export const checkNotSkeletonProgram = (encounter: PatientEncounter | null): boolean => {
  if (!encounter) return false;
  
  const program = encounter.patientPaymentProgram?.[
    encounter.patientPaymentProgram.length - 1
  ];
  
  if (!program) return false;
  
  const schedule = program.patientPaymentSchedule;
  const isFullPay = schedule?.paymentFreq === 'F';
  
  // Full pay plans are always valid
  if (isFullPay) return true;
  
  const downPaymentAmt = program.downPmtAmt || 0;
  const recurringAmt = schedule?.paymentDueAmt || 0;
  const terms = schedule?.terms || 0;
  
  // If both down payment and recurring are zero/minimal, it's a skeleton program
  const isZeroDown = downPaymentAmt === 0;
  const hasValidRecurring = recurringAmt >= 1 || terms >= 1;
  
  // Skeleton program: zero down AND no valid recurring setup
  if (isZeroDown && !hasValidRecurring) {
    return false;
  }
  
  return true;
};

/**
 * Check if down payment will be satisfied after current payment.
 */
export const checkDownPaymentWillBeSatisfied = (
  encounter: PatientEncounter | null,
  currentPaymentAmount: number = 0,
  pendingRemainingBalance?: number
): boolean => {
  if (!encounter) return false;
  
  const program = encounter.patientPaymentProgram?.[
    encounter.patientPaymentProgram.length - 1
  ];
  
  if (!program) return false;
  
  const isFullPay = program.patientPaymentSchedule?.paymentFreq === 'F';
  
  // Full pay plans skip this check
  if (isFullPay) return true;
  
  const downPaymentRequired = program.downPmtAmt || 0;
  
  // If zero-down plan, always satisfied
  if (downPaymentRequired === 0) {
    return true;
  }
  
  const pfrAmt = program.patientPaymentSchedule?.pfrAmt || encounter.pfrAmt || 0;
  
  // Calculate previous payments made toward down payment
  // Previous payments = PFR - Pending Remaining Balance
  const remainingBalance = pendingRemainingBalance ?? pfrAmt;
  const previousPayments = pfrAmt - remainingBalance;
  
  // Total payments after this transaction
  const totalPaymentsAfterCurrent = previousPayments + currentPaymentAmount;
  
  // Check if total payments will satisfy the down payment requirement
  const isSatisfied = totalPaymentsAfterCurrent >= downPaymentRequired;

  
  return isSatisfied;
};

export const checkRecurringPaymentDate = (
  encounter: PatientEncounter | null,
  recurringPaymentDate?: string
): boolean => {
  if (!encounter) return false;
  
  const program = encounter.patientPaymentProgram?.[
    encounter.patientPaymentProgram.length - 1
  ];
  
  if (!program) return false;
  
  const isFullPay = program.patientPaymentSchedule?.paymentFreq === 'F';
  
  if (isFullPay) return true;
  
  return !!recurringPaymentDate && recurringPaymentDate.trim() !== '';
};

export const checkRecurringPaymentDateNotPast = (
  encounter: PatientEncounter | null,
  recurringPaymentDate?: string
): boolean => {
  if (!encounter) return false;
  
  const program = encounter.patientPaymentProgram?.[
    encounter.patientPaymentProgram.length - 1
  ];
  
  if (!program) return false;
  
  const isFullPay = program.patientPaymentSchedule?.paymentFreq === 'F';
  
  if (isFullPay) return true;
  
  if (!recurringPaymentDate || recurringPaymentDate.trim() === '') {
    return true;
  }
  
  const selectedDate = new Date(recurringPaymentDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  return selectedDate >= today;
};

/**
 * Check if recurring payment date is within 60 days from today.
 */
export const checkRecurringPaymentDateWithin60Days = (
  encounter: PatientEncounter | null,
  recurringPaymentDate?: string
): boolean => {
  if (!encounter) return false;
  
  const program = encounter.patientPaymentProgram?.[
    encounter.patientPaymentProgram.length - 1
  ];
  
  if (!program) return false;
  
  const isFullPay = program.patientPaymentSchedule?.paymentFreq === 'F';
  
  if (isFullPay) return true;
  
  if (!recurringPaymentDate || recurringPaymentDate.trim() === '') {
    return true;
  }
  
  const selectedDate = new Date(recurringPaymentDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  // Calculate the date 60 days from today
  const maxDate = new Date(today);
  maxDate.setDate(maxDate.getDate() + 60);
  
  return selectedDate <= maxDate;
};

/**
 * Check if recurring payment date is not on the 29th, 30th, or 31st.
 */
export const checkRecurringPaymentDateValidDay = (
  encounter: PatientEncounter | null,
  recurringPaymentDate?: string
): boolean => {
  if (!encounter) return false;
  
  const program = encounter.patientPaymentProgram?.[
    encounter.patientPaymentProgram.length - 1
  ];
  
  if (!program) return false;
  
  const isFullPay = program.patientPaymentSchedule?.paymentFreq === 'F';
  
  if (isFullPay) return true;
  
  if (!recurringPaymentDate || recurringPaymentDate.trim() === '') {
    return true;
  }
  
  const selectedDate = new Date(recurringPaymentDate);
  const dayOfMonth = selectedDate.getDate();
  
  return dayOfMonth <= 28;
};
